import axios from 'axios';
import { supabaseApi, parseFormData } from './supabaseApi';

// Salva o adapter padrão caso precise repassar APIs externas (ex: ViaCEP)
const defaultAdapter = axios.defaults.adapter;

function normalizePath(rawUrl) {
  let url = rawUrl || '';
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const parsed = new URL(url);
      url = parsed.pathname;
    }
  } catch {
    url = url.replace(/^https?:\/\/[^/]+/, '');
  }
  const cleanPath = url.split('?')[0].replace(/\/+$/, '');
  return cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
}

export async function supabaseAxiosAdapter(config) {
  const rawUrl = config.url || '';
  const method = (config.method || 'get').toLowerCase();

  // Se a requisição NÃO for para a nossa API antiga (Railway / localhost / /api), repassa para o fetch padrão (ex: ViaCEP)
  const isInternalApi =
    rawUrl.includes('railway.app') ||
    rawUrl.includes('localhost') ||
    rawUrl.startsWith('/') ||
    rawUrl.startsWith('api/');

  if (!isInternalApi) {
    if (typeof defaultAdapter === 'function') {
      return defaultAdapter(config);
    }
    const fetchAdapter = axios.getAdapter && axios.getAdapter('fetch');
    if (typeof fetchAdapter === 'function') {
      return fetchAdapter(config);
    }
    return defaultAdapter ? defaultAdapter(config) : axios(config);
  }

  const path = normalizePath(rawUrl);
  const data = config.data;
  let result = null;
  let status = method === 'post' ? 201 : 200;

  try {
    // 1. AUTENTICAÇÃO E ADMIN
    if (path === '/auth/login/admin' && method === 'post') {
      const payload = parseFormData(data);
      const res = await supabaseApi.auth.loginAdmin(payload.email, payload.senha);
      result = { accessToken: res.accessToken, usuario: res.usuario };
    } else if (path === '/auth/login/author' && method === 'post') {
      const payload = parseFormData(data);
      const res = await supabaseApi.auth.loginAuthor(payload.email, payload.senha);
      result = { accessToken: res.accessToken, usuario: res.usuario };
    } else if (path === '/create-account' && method === 'post') {
      const res = await supabaseApi.auth.createAdminAccount(data);
      result = res;

    // 2. EVENTOS
    } else if (path === '/eventos' && method === 'get') {
      result = await supabaseApi.eventos.getAll();
    } else if (path.startsWith('/eventos/') && method === 'get') {
      const slug = decodeURIComponent(path.replace('/eventos/', ''));
      result = await supabaseApi.eventos.getBySlug(slug);
    } else if (path === '/eventos' && method === 'post') {
      const res = await supabaseApi.eventos.create(data, data);
      result = res;
    } else if (path.startsWith('/eventos/') && method === 'put') {
      const slug = decodeURIComponent(path.replace('/eventos/', ''));
      result = await supabaseApi.eventos.update(slug, data, data);

    // 3. AUTORES
    } else if (path === '/get-authors' && method === 'get') {
      result = await supabaseApi.autores.getAll();
    } else if (path.startsWith('/get-author/') && method === 'get') {
      const id = path.replace('/get-author/', '');
      result = await supabaseApi.autores.getById(id);
    } else if (path === '/create-author' && method === 'post') {
      result = await supabaseApi.autores.create(data, data);
    } else if (path === '/complete-info-author' && method === 'post') {
      result = await supabaseApi.autores.completeInfo(data);

    // 4. LIVROS
    } else if (path === '/get-books' && method === 'get') {
      result = await supabaseApi.livros.getAll();
    } else if (path.startsWith('/get-book/') && method === 'get') {
      const id = path.replace('/get-book/', '');
      result = await supabaseApi.livros.getById(id);
    } else if (path === '/create-book' && method === 'post') {
      result = await supabaseApi.livros.create(data, data);

    // 5. ARTISTAS
    } else if (path === '/artists' && method === 'get') {
      const artists = await supabaseApi.artistas.getAll();
      result = { success: true, data: artists };
    } else if (path.startsWith('/artists/') && method === 'get') {
      const id = path.replace('/artists/', '');
      const artist = await supabaseApi.artistas.getById(id);
      result = { success: true, data: artist };
    } else if (path === '/artists' && method === 'post') {
      result = await supabaseApi.artistas.create(data, data);
    } else if (path.startsWith('/artists/') && method === 'put') {
      const id = path.replace('/artists/', '');
      result = await supabaseApi.artistas.update(id, data, data);
    } else if (path.startsWith('/artists/') && method === 'delete') {
      const id = path.replace('/artists/', '');
      result = await supabaseApi.artistas.delete(id);

    // 6. CONCURSO DE POEMAS
    } else if (path === '/register-poem' && method === 'post') {
      result = await supabaseApi.poema.register(data, data);

    } else {
      console.warn(`[SupabaseAdapter] Rota não mapeada: ${method.toUpperCase()} ${path}`);
      // Fallback para caso tente chamar alguma rota diferente
      result = { message: 'Operação concluída.' };
    }

    // Compatibilidade dupla: atende chamadas que leem res.data diretamente ou res.data.data
    let responseData;
    if (Array.isArray(result)) {
      responseData = [...result];
      responseData.data = result;
    } else if (typeof result === 'object' && result !== null) {
      responseData = { ...result };
      if (responseData.data === undefined) {
        responseData.data = result;
      }
    } else {
      responseData = result;
    }

    return {
      data: responseData,
      status,
      statusText: 'OK',
      headers: { 'content-type': 'application/json' },
      config,
    };
  } catch (error) {
    console.error(`[SupabaseAdapter] Erro na requisição ${method.toUpperCase()} ${path}:`, error);

    const errorStatus = error.status || (error.message?.includes('não encontrado') ? 404 : 400);
    const axiosError = new Error(error.message || 'Erro interno na requisição');
    axiosError.name = 'AxiosError';
    axiosError.isAxiosError = true;
    axiosError.config = config;
    axiosError.response = {
      data: {
        success: false,
        message: error.message || 'Erro na requisição',
        error: error.message,
      },
      status: errorStatus,
      statusText: 'Error',
      headers: { 'content-type': 'application/json' },
      config,
    };

    throw axiosError;
  }
}

// Ativa o adaptador no Axios globalmente
axios.defaults.adapter = supabaseAxiosAdapter;
